import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthService } from './auth.service';
import { TenantService } from '../shared/tenant.service';
import { getTenant, getTenantKey } from '../shared/tenant.util';
import { environment } from '../../environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private token: string | null = null;

  constructor(private authService: AuthService, private tenantService: TenantService) {
    //  Subscribe to token updates globally
    this.authService.token$.subscribe(token => {
      this.token = token;
    });
  }

  /**
   * Marks the request as coming from an SM front-end (SM.API: ApiClientGateMiddleware).
   *
   * The SM.Auth cookie is ambient — the browser attaches it to anything aimed at the API host,
   * including a URL typed into the address bar. This header is NOT a secret (it ships in this
   * bundle); it works because a browser never sends it on its own, so the backend can tell our
   * XHR apart from a navigation. Scoped to the tenant backend so it goes nowhere else.
   */
  private withClientKey(req: HttpRequest<any>): HttpRequest<any> {
    const apiBase = getTenant();
    if (!apiBase || !req.url.startsWith(apiBase)) {
      return req;
    }
    return req.clone({
      setHeaders: {
        'X-SM-Client-Key': environment.smClientKey,
        // The key is common to every tenant because this bundle is. This states WHICH tenant we
        // resolved, and the backend refuses it unless it matches its own - so an editor tab whose
        // stored tenant no longer matches the backend it is calling is rejected, not served.
        'X-SM-Tenant': getTenantKey() ?? ''
      }
    });
  }

  intercept(originalReq: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const req = this.withClientKey(originalReq);
    const isFormData = req.body instanceof FormData;

    // Send credentials ONLY for a WS-Fed tenant (e.g. sem1) so the browser attaches
    // the SM.Auth session cookie (established during the angular-ui login) and Form
    // Editor rides that session instead of triggering a federation redirect. MAGBypass
    // tenants (omlmc/omboqa) must NOT send credentials — their backends do not return
    // Access-Control-Allow-Credentials, so a credentialed request would be blocked by
    // the browser and the page would fail to load.
    const useCredentials = this.tenantService.usesWsFed();

    const clonedReq = req.clone({
      ...(useCredentials ? { withCredentials: true } : {}),
      ...(this.token
        ? {
            setHeaders: {
              'accept': '*/*',
              ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
              Authorization: `Bearer ${this.token}`
            }
          }
        : {})
    });

    return next.handle(clonedReq);
  }
  
}
